'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'

export default function NavigationProgress() {
  const pathname = usePathname()
  const [isNavigating, setIsNavigating] = useState(false)
  const [isComplete, setIsComplete] = useState(false)

  // Start progress when user clicks an internal link (<a href="/...">)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const anchor = target.closest('a[href]')
      if (!anchor) return
      const href = (anchor as HTMLAnchorElement).getAttribute('href')
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return
      if ((anchor as HTMLAnchorElement).target === '_blank') return
      if (href.startsWith('/') && href !== pathname) {
        setIsNavigating(true)
        setIsComplete(false)
      }
    }
    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [pathname])

  // Start progress when navigation is triggered programmatically (e.g. router.push from feed)
  useEffect(() => {
    const onNavStart = () => {
      setIsNavigating(true)
      setIsComplete(false)
    }
    window.addEventListener('mmt:navigation-start', onNavStart)
    return () => window.removeEventListener('mmt:navigation-start', onNavStart)
  }, [])

  // Complete progress when pathname changes (new page is loading/loaded)
  useEffect(() => {
    if (!isNavigating) return
    setIsComplete(true)
    const t = setTimeout(() => {
      setIsNavigating(false)
      setIsComplete(false)
    }, 200)
    return () => clearTimeout(t)
  }, [pathname])

  if (!isNavigating && !isComplete) return null

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-0.5 bg-gray-200 overflow-hidden pointer-events-none"
      aria-hidden
    >
      <div
        className={`h-full bg-gray-900 transition-all duration-200 ease-out ${
          !isComplete ? 'animate-nav-progress w-1/3' : 'w-full'
        }`}
      />
    </div>
  )
}
